import * as React from 'react';

import { isAdminOrganization } from '@rematter/common/identity';
import { useAuditLogLocation } from '@rematter/modules/audit-log/_state/auditLogModule.state';
import { useSettingsLocation } from '@rematter/modules/settings/_state/settingsModule.state';
import {
  cn,
  RawTooltip,
  ReMatterLogo,
  TooltipTrigger,
  useScrollVShadow,
} from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideHistory,
  LucidePanelLeftClose,
  LucidePanelLeftOpen,
  LucideSettings,
} from 'lucide-react';
import { Router, useLocation } from 'wouter';
import { useHashLocation } from 'wouter/use-hash-location';

import {
  adminSidebarModules,
  appSidebarModules,
  filterModulesByEntitlements,
} from '../AppRouter';
import { useTranslations } from '../i18n/useTranslations';
import {
  $expandedModules as $expandedModulesState,
  $sidebarWidth as $sidebarWidthState,
  $sidebarCollapsed as $sidebarCollapsedState,
} from '../state/sidebar.state';

import { EnvironmentTag } from './EnvironmentTag';
import {
  iconVariants,
  moduleButtonVariants,
  SidebarModuleItem,
} from './SidebarModuleItem';
import { VersionBadge } from './VersionBadge';

import type { AllPermissionKeys } from '~/modules/settings/_generated/permissions';
import { useIdentity, usePermission, usePermissions } from '~/shared/hooks';
import { useEntitlements } from '~/shared/providers/auth';
import type { NavItem } from '~/shared/routing/createModuleNav';
import {
  filterNavByPermissions,
  pruneNavForSidebar,
} from '~/shared/routing/filterNavByPermissions';
import type { ModuleDefinition } from '~/shared/routing/types';
import {
  $userSidebarModuleOrder,
  $userSidebarSubsectionOrder,
} from '~/shared/state/settings.state';
import { sortModulesByOrder } from '~/shared/utils/sortNavItems';

// Constants for sidebar resizing
const MIN_SIDEBAR_WIDTH = 200; // Minimum width in pixels
const MAX_SIDEBAR_WIDTH = 400; // Maximum width in pixels
const DEFAULT_SIDEBAR_WIDTH = 216; // Default width (w-54 = 13.5rem = 216px)

function hasGatedRoutes(nav: NavItem): boolean {
  if (nav.route.requiredPermission) return true;
  return nav.children.some(hasGatedRoutes);
}

function collectRequiredPermissions(
  modules: ModuleDefinition[],
): AllPermissionKeys[] {
  const permissions = new Set<AllPermissionKeys>();

  function walk(item: NavItem) {
    if (item.route.requiredPermission) {
      permissions.add(item.route.requiredPermission);
    }
    item.children.forEach(walk);
  }

  modules.forEach((module) => {
    if (module.nav) {
      walk(module.nav);
    }
  });

  return [...permissions];
}

/**
 * Sidebar component that displays the main navigation menu.
 * Features collapsible sections with animated transitions and staggered item reveals.
 */
export const Sidebar = () => {
  const { organization } = useIdentity();
  const { entitlements } = useEntitlements();
  const [location] = useLocation();
  const t = useTranslations();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { showTopShadow, showBottomShadow } =
    useScrollVShadow(scrollContainerRef);

  const [expandedModules, setExpandedModules] = useAtom($expandedModulesState);
  const [manuallyCollapsed, setManuallyCollapsed] = React.useState<string[]>(
    [],
  );
  const [sidebarWidth, setSidebarWidth] = useAtom($sidebarWidthState);
  const [isCollapsed, setIsCollapsed] = useAtom($sidebarCollapsedState);
  const [isResizing, setIsResizing] = React.useState(false);
  // Add transition state to handle label fade out before sidebar collapses
  const [isLabelVisible, setIsLabelVisible] = React.useState(!isCollapsed);
  const moduleOrder = useAtomValue($userSidebarModuleOrder);
  const subsectionOrder = useAtomValue($userSidebarSubsectionOrder);

  const { container, header, headerTitle, nav, moduleContainer, resizeHandle } =
    sidebarVariants;

  const isAdminOrg = isAdminOrganization(organization);

  const { allowed: canAccessSettings, isLoading: settingsPermissionLoading } =
    usePermission(isAdminOrg ? '' : 'settingsRead');
  const showSettings =
    isAdminOrg || (!settingsPermissionLoading && canAccessSettings);

  const entitlementFilteredModules = React.useMemo(() => {
    const modules = isAdminOrg
      ? [...appSidebarModules, ...adminSidebarModules]
      : filterModulesByEntitlements(appSidebarModules, entitlements);

    return sortModulesByOrder(modules, moduleOrder);
  }, [isAdminOrg, entitlements, moduleOrder]);

  const requiredPermissions = React.useMemo(
    () =>
      isAdminOrg ? [] : collectRequiredPermissions(entitlementFilteredModules),
    [entitlementFilteredModules, isAdminOrg],
  );

  const { permissions, isLoading: permissionsLoading } =
    usePermissions(requiredPermissions);

  const availableModules = React.useMemo(() => {
    if (isAdminOrg) {
      return entitlementFilteredModules;
    }

    if (permissionsLoading) {
      return entitlementFilteredModules.filter(
        (module) => !module.nav || !hasGatedRoutes(module.nav),
      );
    }

    return entitlementFilteredModules
      .map((module) => {
        if (!module.nav) {
          return module;
        }

        const filteredNav = filterNavByPermissions(module.nav, permissions);
        if (!filteredNav) {
          return null;
        }

        const sidebarNav = pruneNavForSidebar(filteredNav);
        if (!sidebarNav) {
          return null;
        }

        if (sidebarNav === module.nav) {
          return module;
        }

        return { ...module, nav: sidebarNav };
      })
      .filter((module): module is ModuleDefinition => module !== null);
  }, [entitlementFilteredModules, permissions, permissionsLoading, isAdminOrg]);

  /**
   * Auto-expands modules when navigating directly to their subroutes
   * This ensures the parent module is expanded when deep-linking
   */
  React.useEffect(() => {
    if (isCollapsed) {
      setIsLabelVisible(false);
      return;
    }

    availableModules.forEach(({ path, nav: menu }) => {
      if (
        menu?.children?.some(({ route }) => location.startsWith(route.path))
      ) {
        if (
          !expandedModules.includes(path) &&
          !manuallyCollapsed.includes(path)
        ) {
          setExpandedModules((prev) => [...prev, path]);
        }
      }
    });
  }, [
    location,
    availableModules,
    expandedModules,
    setExpandedModules,
    setIsLabelVisible,
    manuallyCollapsed,
    isCollapsed,
    isAdminOrg,
  ]);

  /**
   * Handles expanding/collapsing of modules
   * When expanding: adds to expanded modules and removes from manually collapsed
   * When collapsing: removes from expanded modules and adds to manually collapsed
   */
  const toggleModule = (path: string) => {
    if (isCollapsed) return;

    if (expandedModules.includes(path)) {
      setExpandedModules((prev) => prev.filter((p) => p !== path));
      setManuallyCollapsed((prev) => [...prev, path]);
    } else {
      setExpandedModules((prev) => [...prev, path]);
      setManuallyCollapsed((prev) => prev.filter((p) => p !== path));
    }
  };

  /**
   * Handles the start of resize operation
   */
  const startResize = (e: React.MouseEvent) => {
    if (isCollapsed) return;
    setIsResizing(true);
    e.preventDefault();
  };

  /**
   * Stops the resize operation
   */
  const stopResize = () => {
    setIsResizing(false);
  };

  /**
   * Toggles sidebar between collapsed and expanded states
   */
  const toggleCollapse = React.useCallback(() => {
    if (!isCollapsed) {
      setIsLabelVisible(false);
      setTimeout(() => {
        setIsCollapsed(true);
      }, 100);
    } else {
      setIsCollapsed(false);
      setTimeout(() => {
        setIsLabelVisible(true);
      }, 100);
      setSidebarWidth(sidebarWidth || DEFAULT_SIDEBAR_WIDTH);
    }
  }, [
    isCollapsed,
    sidebarWidth,
    setSidebarWidth,
    setIsLabelVisible,
    setIsCollapsed,
  ]);

  // Set cursor style for the entire page during resize
  React.useEffect(() => {
    const handleResize = (e: MouseEvent) => {
      if (!isResizing) return;

      // Calculate new width based on mouse position
      const newWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(MAX_SIDEBAR_WIDTH, e.clientX),
      );
      setSidebarWidth(newWidth);
    };

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);

    if (isResizing) {
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing, setSidebarWidth]);

  const [, setSettingsLocation] = useSettingsLocation();
  const [, setAuditLogLocation] = useAuditLogLocation();

  return (
    <aside
      className={cn(
        '@Sidebar',
        container({
          collapsed: isCollapsed,
          resizing: isResizing,
        }),
      )}
      style={{
        width: isCollapsed ? undefined : sidebarWidth || DEFAULT_SIDEBAR_WIDTH,
      }}
    >
      <div
        className={header({
          collapsed: isCollapsed,
        })}
      >
        <div
          className={cn(
            'flex w-full items-center justify-between',
            isCollapsed && 'justify-center',
          )}
        >
          {!isCollapsed && (
            <span
              className={cn(
                headerTitle(),
                isCollapsed ? 'ml-1 scale-75' : '',
                'flex items-center gap-2',
              )}
            >
              <ReMatterLogo
                size={isCollapsed ? 'small' : 'large'}
                height={28}
              />
              <EnvironmentTag />
            </span>
          )}
          <TooltipTrigger>
            <button
              onClick={toggleCollapse}
              className={cn(
                'flex size-8 cursor-pointer items-center justify-center rounded-md bg-white/10 p-1.5 transition-colors duration-150 hover:bg-white/20',
                isCollapsed && 'border border-primary/20 bg-primary',
              )}
            >
              {isCollapsed ? (
                <LucidePanelLeftOpen
                  className="size-4 text-white"
                  strokeWidth={1.5}
                />
              ) : (
                <LucidePanelLeftClose
                  className="size-4 text-white"
                  strokeWidth={1.5}
                />
              )}
            </button>
            {isCollapsed ? (
              <RawTooltip placement="right" offset={8}>
                {t.sidebar.expand}
              </RawTooltip>
            ) : (
              <RawTooltip placement="right" offset={8}>
                {t.sidebar.collapse}
              </RawTooltip>
            )}
          </TooltipTrigger>
        </div>
      </div>
      <nav className={nav({ collapsed: isCollapsed })}>
        <div className="relative flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="no-scrollbar flex size-full flex-col gap-1 overflow-x-hidden overflow-y-auto"
          >
            {availableModules.map((module) => (
              <div key={module.path} className={moduleContainer()}>
                <SidebarModuleItem
                  module={module}
                  isCollapsed={isCollapsed}
                  isLabelVisible={isLabelVisible}
                  expandedModules={expandedModules}
                  location={location}
                  toggleModule={toggleModule}
                  subsectionOrder={subsectionOrder[module.path]}
                />
              </div>
            ))}
          </div>
          {/* Top shadow */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 h-3 bg-linear-to-b from-white to-transparent transition-opacity duration-300',
              showTopShadow ? 'opacity-100' : 'opacity-0',
            )}
          />
          {/* Bottom shadow */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-linear-to-t from-white to-transparent transition-opacity duration-300',
              showBottomShadow ? 'opacity-100' : 'opacity-0',
            )}
          />
        </div>
        <div className="border-t border-neutral/5 pt-2">
          {showSettings && (
            <TooltipTrigger>
              {/*
               * 🟠🟠 We need to use a router here because the settings module is configured to use hash location.
               */}
              <Router hook={useHashLocation}>
                <a
                  href={'/#'}
                  className={cn(
                    moduleButtonVariants({
                      active: false,
                    }),
                    isCollapsed && 'justify-center p-2.5',
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setSettingsLocation('/');
                  }}
                >
                  <div className="flex grow flex-row items-center">
                    <LucideSettings
                      className={iconVariants({ collapsed: isCollapsed })}
                    />
                    {!isCollapsed && (
                      <>
                        <div
                          className={cn(
                            'grow transition-opacity duration-200',
                            isLabelVisible ? 'opacity-100' : 'opacity-0',
                          )}
                        >
                          {t.sidebar.settings}
                        </div>
                        <div className="**:text-inherit!">
                          <VersionBadge collapsed={isCollapsed} />
                        </div>
                      </>
                    )}
                  </div>
                </a>
              </Router>
              {isCollapsed && (
                <RawTooltip placement="right" offset={8}>
                  {t.sidebar.settings}
                </RawTooltip>
              )}
            </TooltipTrigger>
          )}
          {isAdminOrg && (
            <TooltipTrigger>
              <Router hook={useHashLocation}>
                <a
                  href={'/#'}
                  className={cn(
                    moduleButtonVariants({
                      active: false,
                    }),
                    isCollapsed && 'justify-center p-2.5',
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setAuditLogLocation('/');
                  }}
                >
                  <div className="flex grow flex-row items-center">
                    <LucideHistory
                      className={iconVariants({ collapsed: isCollapsed })}
                    />
                    {!isCollapsed && (
                      <div
                        className={cn(
                          'grow transition-opacity duration-200',
                          isLabelVisible ? 'opacity-100' : 'opacity-0',
                        )}
                      >
                        {t.sidebar.auditLog}
                      </div>
                    )}
                  </div>
                </a>
              </Router>
              {isCollapsed && (
                <RawTooltip placement="right" offset={8}>
                  {t.sidebar.auditLog}
                </RawTooltip>
              )}
            </TooltipTrigger>
          )}
        </div>
      </nav>
      {/* Resize handle - only show when not collapsed */}
      {!isCollapsed && (
        <div className={resizeHandle()} onMouseDown={startResize} />
      )}
    </aside>
  );
};

const sidebarVariants = {
  container: cva('relative flex h-screen flex-col bg-white shadow', {
    variants: {
      collapsed: {
        true: 'w-14 overflow-hidden',
      },
      resizing: {
        true: '',
        false: 'transition-all duration-300',
      },
    },
  }),
  header: cva(
    'flex h-14 min-h-14 items-center justify-center border-r border-neutral/70 bg-neutral',
    {
      variants: {
        collapsed: {
          true: '',
          false: 'pr-2 pl-6',
        },
      },
    },
  ),
  headerTitle: cva('text-xl font-semibold text-white transition-all'),
  nav: cva(
    [
      'flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden border-r border-neutral/20 px-2 py-4',
    ],
    {
      variants: {
        collapsed: {
          true: 'px-1',
          false: '',
        },
      },
    },
  ),
  moduleContainer: cva(''),
  resizeHandle: cva(
    'absolute top-0 right-0 z-10 h-full w-1 cursor-ew-resize bg-transparent transition-colors duration-150 hover:bg-neutral/30',
  ),
};
